import { useEmployeesStore } from '@/stores/Employees'
import { useSettingsStore } from '@/stores/Settings'
import { useMediaDevicesStore } from '@/stores/MediaDevices'
import { useApiStore } from '@/stores/Api'
import { useElectronStore } from '@/stores/Electron'
import { usePhonebookStore } from '@/stores/Phonebook'

import globalEvents from '@/utilities/GlobalEvents'
import logger from '@/utilities/Logger'
import { sleep } from '@/utilities/Tools'
import _i18n from '@/utilities/I18n'

import { AxiosError } from 'axios'
import { UAParser } from 'ua-parser-js'
import JsSIP from '@per_moeller/jssip-telecomx'

import { Originator } from '@per_moeller/jssip-telecomx/lib/RTCSession'
import { IncomingRequest, IncomingResponse } from '@per_moeller/jssip-telecomx/lib/SIPMessage'
import type { IncomingRTCSessionEvent, OutgoingRTCSessionEvent } from '@per_moeller/jssip-telecomx/lib/UA'

import Jabra from '@/utilities/headsets/Jabra'
import Sennheiser from '@/utilities/headsets/Sennheiser'
import Yealink from '@/utilities/headsets/Yealink'
import YealinkElectron from '@/utilities/headsets/YealinkElectron'
import Kuando from '@/utilities/headsets/Kuando'

import { SipClientEndReason, SipClientEvent, SipClientTcxState, type SipClientRTCSession, type SipClientUA } from '@/utilities/SipClient.types'
import type { PbxSipPhoneWebRTCRequest, PbxSipPhoneWebRTCV2Response } from '@/TcxApi/PbxSipPhone'
import { MediaDeviceType } from '@/stores/MediaDevices.types'
import { ref, type Ref, watch } from 'vue'
import type { PbxCallState } from '@/TcxApi/PbxCommon'

const browserDetect = UAParser()
const isChrome = ['Chrome', 'Chromium', 'Electron'].includes(browserDetect.browser.name ?? '')
let mediaDevices: ReturnType<typeof useMediaDevicesStore>
let employees: ReturnType<typeof useEmployeesStore>
let ss: ReturnType<typeof useSettingsStore>
const i18n = _i18n.global
let api: ReturnType<typeof useApiStore>

const CALL_WAITING_RINGTONE = '60af988785fa792a658f75f3'
// const NOTIFICATION_TONE = '60af932685fa792a658f75e9' // eslint-disable-line
const RINGING_TONE = '60bf38d87a40e725dd7fa219' // '60b0dd9fd2d02e586ee051ff'
const BUSY_TONE = '60bf38aa7a40e725dd7fa217' // '60b0e2a9d2d02e586ee05203'
// const BUSY_TONE_SHORT = '60bf38ca7a40e725dd7fa218' // eslint-disable-line
// const ONE_SECOND_SILENCE = '60af951485fa792a658f75eb' // eslint-disable-line
const DTMF_TONES = {
	0: '60bf53db7a40e725dd7fa249',
	1: '60bf54347a40e725dd7fa24b',
	2: '60bf543e7a40e725dd7fa24c',
	3: '60bf544a7a40e725dd7fa24d',
	4: '60bf54557a40e725dd7fa24e',
	5: '60bf54617a40e725dd7fa24f',
	6: '60bf546d7a40e725dd7fa250',
	7: '60bf547c7a40e725dd7fa251',
	8: '60bf54877a40e725dd7fa252',
	9: '60bf54937a40e725dd7fa253',
	'*': '60bf549d7a40e725dd7fa254',
	'#': '60bf54a67a40e725dd7fa255'
}
const MAX_RENEGOTATIONS = 5
const MAX_RETRIES_FAILED_RTC = 10
/**
 * How long to wait for a STUN (srflx) candidate after the last ICE candidate before sending the
 * INVITE with the candidates we have. With a wedged network interface (seen on multi-homed Macs
 * that have been up for weeks) Chrome never delivers a srflx candidate and never completes ICE
 * gathering, which otherwise blocks the INVITE for minutes. A healthy STUN response arrives well
 * under a second, so this only kicks in when STUN is broken - and the call still works without
 * the srflx candidate because the media gateway latches onto the actual RTP source address.
 */
const ICE_GATHERING_WATCHDOG_MS = 2000
/** Safety net: release the headset this long after the busy tone starts if neither 'ended' nor 'error' ever fires */
const BUSY_TONE_MAX_MS = 10000

JsSIP.debug.disable()
// | JsSIP.debug.enable('JsSIP:*');

interface VoiceChangerSet {
	audioContext: AudioContext
	source: MediaStreamAudioSourceNode
	filter: BiquadFilterNode
	destination: MediaStreamAudioDestinationNode
}

export default class SipClient {
	static #instance: SipClient

	private ua: SipClientUA | null = null

	private shouldBePlayingRingtone = false

	registered: Ref<boolean>

	sessions: SipClientRTCSession[]

	private config?: PbxSipPhoneWebRTCV2Response = undefined

	extensionName?: string = undefined

	private isRegisterScheduled = false

	headsets: {
		jabra: Jabra
		sennheiser: Sennheiser
		yealink: Yealink | YealinkElectron
		kuando: Kuando
	}

	voiceChangerEnabled = false

	voiceChangers: VoiceChangerSet[] = []

	alertTimer: ReturnType<typeof setTimeout> | null = null

	/** Cleanup handler bound to the currently playing busy tone, so a new busy tone can detach the previous one */
	private busyToneCleanup?: (event?: Event) => void = undefined

	ignoreUnloadEvent = false

	/** Constructor */
	private constructor() {
		logger.debug('SipClient: Constructor')

		this.registered = ref(false)
		this.sessions = []

		this.headsets = {
			jabra: Jabra.create(this.headsetEventHandler.bind(this)),
			sennheiser: Sennheiser.create(this.headsetEventHandler.bind(this)),
			// Embedded in the desktop shell, Yealink headsets are driven through the native SDK in the main process - in a browser we use WebHID
			yealink: YealinkElectron.isAvailable() ? YealinkElectron.create(this.headsetEventHandler.bind(this)) : Yealink.create(this.headsetEventHandler.bind(this)),
			kuando: Kuando.instance
		}

		mediaDevices = useMediaDevicesStore()
		ss = useSettingsStore()
		api = useApiStore()
		employees = useEmployeesStore()

		globalEvents.on('Auth:LoggedOut', () => { this.deregister() })

		globalEvents.on('Auth:LoggedIn', async () => {
			if (ss.settings.softphoneId && !this.registered.value) {
				if (this.ua) {
					this.deregister()
					await sleep(500)
				}
				this.register()
			}
		})

		globalEvents.on('MediaDeviceChange', device => {
			const micChanged = device.kind === MediaDeviceType.audioinput

			this.updateMediaDevices(micChanged)
		})

		globalEvents.on('MediaDeviceSelected', data => {
			const micChanged = data.type === MediaDeviceType.audioinput

			this.updateMediaDevices(micChanged)
		})

		globalEvents.on('Wakeup', async () => {
			if (this.ua) {
				logger.debug('SipClient: Wakeup detected - de/re-registering SIP client')
				this.deregister()
				await sleep(500)
				this.register()
			}
		})

		globalEvents.on('StatusRequest', () => {
			globalEvents.emit('StatusReport', { key: 'sipClient', value: !!ss.settings.softphoneId })
			if (ss.settings.softphoneId) {
				globalEvents.emit('StatusReport', { key: 'sipClientRegistered', value: this.registered.value })
				globalEvents.emit('StatusReport', { key: 'sipClientSessions', value: this.sessions.length })
				globalEvents.emit('StatusReport', { key: 'sipClientMicrophone', value: mediaDevices.getDeviceName(mediaDevices.microphoneDeviceId) })
				globalEvents.emit('StatusReport', { key: 'sipClientSpeaker', value: mediaDevices.getDeviceName(mediaDevices.speakerDeviceId) })
				globalEvents.emit('StatusReport', { key: 'sipClientVideo', value: mediaDevices.getDeviceName(mediaDevices.videoDeviceId ?? '') })
				globalEvents.emit('StatusReport', { key: 'phoneId', value: ss.settings.softphoneId })
			}
		})

		globalEvents.on('SipClient:Error:Retry', () => { window.location.reload() })

		globalEvents.on('SipClient:Error:Terminate', () => {
			if (ss.runtime.isEmbedded) {
				const electron = useElectronStore()

				electron.terminate()
			} else {
				window.location.href = 'https://blank.page'
			}
		})

		globalEvents.on('Call:Dial', data => {
			this.call(data.number)
		})

		window.addEventListener('beforeunload', (event: BeforeUnloadEvent) => {
			if (this.ignoreUnloadEvent) {
				this.ignoreUnloadEvent = false
				return
			}
			if (this.registered.value) { this.deregister(true) }

			// If we are not embedded, and we have active calls, we should prevent the page from closing before the user confirms
			if (!ss.runtime.isEmbedded && this.sessions.length > 0) {
				event.preventDefault()

				try { (event as { returnValue: string }).returnValue = '' }
				catch (_) { /** Ignore */ }
			}
		})

		watch(() => this.registered.value, async value => {
			// Ensure we only do this when softphone is enabled
			if (ss.settings.softphoneId) {
				// If we are not registered, schedule a timer to show a message in 30 seconds
				if (!value) {
					this.alertTimer = setTimeout(() => {
						if (!ss.questionModalVisible) {
							globalEvents.emit('CommonQuestionModal', {
								header: i18n.t('sipClient.errorHeader'),
								message: i18n.t('sipClient.errorFailedToAuthenticateFatal'),
								ok: i18n.t('sipClient.errorRetry'),
								cancel: i18n.t('sipClient.errorTerminate'),
								emit: 'SipClient:Error:Retry',
								cancelledEmit: 'SipClient:Error:Terminate'
							})
						}
					}, 30000)
				} else {
					// If we are registered, clear the timer and if the message is shown, dismiss it
					if (this.alertTimer) {
						clearTimeout(this.alertTimer)
						this.alertTimer = null
					}

					if (ss.questionModalVisible && ss.questionModalCurrent?.message === i18n.t('sipClient.errorFailedToAuthenticateFatal')) {
						globalEvents.emit('CommonQuestionModalDismiss')
					}
				}
			}
		})
	}

	/** Get the SipClient singleton instance */
	static get instance() {
		SipClient.create()
		return SipClient.#instance
	}

	/** Create the SipClient singleton instance */
	static create() {
		if (!SipClient.#instance) {
			SipClient.#instance = new SipClient()
		}
	}

	private get sipPhoneDeviceId() {
		return this.config?._id ?? null
	}

	/**
	 * Updates media devices when changes
	 * @param micChanged True if microphone has changed
	 */
	private async updateMediaDevices(micChanged = false) {
		if (micChanged && this.sessions.length > 0) {
			logger.debug('SipClient: Media device change detected - while in call - switching audio device')
			await this.switchAudioDevice()
		}
	}

	/** Invoke this to initialize the sip client when it is choosen as the phone to use */
	async register() {
		if (this.isRegisterScheduled) {
			logger.info('SipClient: Register invoked - but ignored due to scheduled register')
			return
		}
		logger.info('SipClient: Register invoked')
		if (this.ua) {
			logger.error('SipClient: Register invoked - but a UA already exists - cleaning up')
			this.ua.stop()
			this.ua.removeAllListeners()
			this.ua = null
			await sleep(500)
		}
		await mediaDevices.init()
		await this.updateMediaDevices()
		this.headsets.kuando.startup()

		// Make sure we have a SIP acccount to register to
		logger.info(`SipClient: Requesting SIP credentials for instance: ${ss.runtime.instanceId} using v2 protocols`)

		try {
			const res = await api.post<PbxSipPhoneWebRTCV2Response>('/pbx/sipphone/webrtc', { instanceId: ss.runtime.instanceId, v2: true } as PbxSipPhoneWebRTCRequest)

			this.config = res.data
			if (ss.settings.softphoneId !== this.sipPhoneDeviceId) {
				ss.settings.softphoneId = this.sipPhoneDeviceId as string
			}
			if (ss.settings.phoneId) {
				ss.settings.phoneId = null
			}
		}
		catch (error) {
			const err = error as AxiosError

			if ((err.status === 0 && err.code === 'network') || err.message?.toLowerCase().includes('econnreset')) { // Network issue, try again in 5 secs
				this.isRegisterScheduled = true
				await sleep(5000)
				this.isRegisterScheduled = false
				this.register()
			} else {
				logger.error(`SipClient: Failed to get SIP credentials: ${err.message}`)
				globalEvents.emit('CommonErrorModal', { header: i18n.t('sipClient.errorHeader'), message: i18n.t('sipClient.errorWebRtcInitFailedMessage') + '<br><br>' + err.message })
			}
			return
		}

		if (!this.config) { return }

		logger.info(`SipClient: SIP credentials received for username: ${this.config.username} and deviceId: ${this.sipPhoneDeviceId} - now initializing JsSIP`)

		// Initialize JsSIP
		const socket = new JsSIP.WebSocketInterface(this.config.server)
		const config = {
			sockets: [ socket ],
			uri: `sip:${this.config.username}@${this.config.server.split('://')[1]}`,
			password: this.config.password,
			realm: 'telecomx.dk',
			display_name: this.extensionName,
			user_agent: `Telecom X ${ss.runtime.appName} ${ss.runtime.version}`,
			session_timers_refresh_method: 'invite'
		}

		this.ua = new JsSIP.UA(config)

		// Ensuring that we do not have multiple JSSIP UA's running at the same time - which has been a pain in the a..
		this.ua.tcxTag = Math.random().toString()
		this.ua.tcxCreated = new Date()
		if ((window as unknown as { sipClientUA: SipClientUA }).sipClientUA) {
			const other = (window as unknown as { sipClientUA: SipClientUA }).sipClientUA

			logger.error(`SipClient: Register detected other sipClient with tag ${other.tcxTag} created: ${other.tcxCreated?.toISOString()} - we will now try to kill it!`)
			other.stop()
			other.removeAllListeners()
		}
		(window as unknown as { sipClientUA: SipClientUA }).sipClientUA = this.ua

		this.ua.on('connecting', data => { // (socket, attempts)
			globalEvents.emit('SipClient', { event: SipClientEvent.CONNECTING, attempt: data.attempts })
			logger.info(`SipClient: Connecting - attempt ${data.attempts}`)
		})

		this.ua.on('connected', _ => { // (socket)
			globalEvents.emit('SipClient', { event: SipClientEvent.CONNECTED })
			logger.info('SipClient: Connected')
		})

		this.ua.on('disconnected', data => { // (socket, error, code, reason)
			globalEvents.emit('SipClient', { event: SipClientEvent.DISCONNECTED, code: data.code, reason: data.reason })
			logger.info(`SipClient: Disconnected: ${data.error ? 'Due to error - ' : ''}${data.code} - ${data.reason}`)
		})

		this.ua.on('registered', data => { // Response
			this.registered.value = true
			globalEvents.emit('SipClient', { event: SipClientEvent.REGISTERED })
			logger.info(`SipClient: Registered - ${data.response.status_code} ${data.response.reason_phrase}`)
		})

		this.ua.on('unregistered', data => { // (response, cause)
			this.registered.value = false
			globalEvents.emit('SipClient', { event: SipClientEvent.UNREGISTERED, cause: data.cause })
			logger.info(`SipClient: Unregistered - ${data.response ? data.response.status_code : 'n/a'} ${data.response ? data.response.reason_phrase : 'n/a'} - ${data.cause}`)
		})

		this.ua.on('registrationFailed', data => {
			this.registered.value = false

			if (!data.response) {
				this.ua?.stop()
				logger.error('SipClient: Registration failed - Response missing - SIP client terminated - will retry in 5 seconds')
				globalEvents.emit('SipClient', { event: SipClientEvent.REGISTERFAILED, code: 0, reason: 'UNKNOWN' })
				setTimeout(() => { this.register() }, 5000)
				return
			}

			if (data.response.status_code === 403) {
				this.ua?.stop()
				logger.error(`SipClient: Registration failed - ${data.cause} - ${data.response.status_code} ${data.response.reason_phrase} - SIP client terminated`)
			} else {
				logger.error(`SipClient: Registration failed - ${data.cause} - ${data.response.status_code} ${data.response.reason_phrase}`)
			}

			globalEvents.emit('SipClient', { event: SipClientEvent.REGISTERFAILED, code: data.response.status_code, reason: data.response.reason_phrase })

			if (data.response.status_code === 403) {
				globalEvents.emit('CommonQuestionModal', {
					header: i18n.t('sipClient.errorHeader'),
					message: i18n.t('sipClient.errorFailedToAuthenticateFatal'),
					ok: i18n.t('sipClient.errorRetry'),
					cancel: i18n.t('sipClient.errorTerminate'),
					emit: 'SipClient:Error:Retry',
					cancelledEmit: 'SipClient:Error:Terminate'
				})
			} else {
				globalEvents.emit('CommonErrorModal', {
					header: i18n.t('sipClient.errorHeader'),
					message: i18n.t('sipClient.errorFailedToAuthenticate').replace('%%code%%', `${data.response.status_code}`).replace('%%reason%%', data.response.reason_phrase)
				})
			}
		})

		this.ua.on('newRTCSession', (data: IncomingRTCSessionEvent | OutgoingRTCSessionEvent) => { // incoming call (originator, session, request)
			if (data.originator === 'local') {
				if (!data.session.data) { data.session.data = {} }
				data.session.data.callId = data.request.getHeader('Call-ID')
				logger.info(`SipClient: Outbound call with Call-ID ${data.session.data.callId} initiated`)
				const rtcPeerConnection = data.session.connection

				rtcPeerConnection.addEventListener('track', async event => {
					logger.info('SipClient: Received track with remote audio for outbound call')
					this.createRemoteAudioPlayer(data.session, event.streams[0] as MediaStream)
				})
			} else {
				this.onIncomingCall(data)
			}
		})

		this.ua.start()
	}

	/**
	 * Invoke this to terminate the SIP client when it is not needed anymore
	 * @param appExit True if the app is exiting
	 */
	deregister(appExit?: boolean) {
		logger.info('SipClient: Deregister invoked')
		if (this.ua) {
			this.ua.stop()
			this.ua.removeAllListeners()
			this.ua = null
		}

		this.registered.value = false
		this.sessions = []
		this.config = undefined

		if (!appExit) {
			ss.settings.softphoneId = null
		}
		this.headsets.kuando.off()
		if (this.headsets.jabra.use.value) { this.headsets.jabra.onHook() }
		if (this.headsets.yealink.use.value) { this.headsets.yealink.onHook() }
		if (this.headsets.sennheiser.use.value) { this.headsets.sennheiser.reInit() }
	}

	/**
	 * Invoked when an incoming call is recieved - will initialize, announce and add to list of calls
	 * @param data Event data (originator, session, request)
	 */
	private onIncomingCall(data: IncomingRTCSessionEvent | OutgoingRTCSessionEvent) {
		// Parse data, store session and emit event
		const caller = this.parseUri(data.request.getHeader('From'))
		const callee = this.parseUri(data.request.getHeader('To'))

		// The From display name is often just the number or empty - resolve a real name from
		// colleagues or the phonebook, like the UI does for realtime call events
		if (!caller.name || caller.name === caller.number) {
			caller.name = employees.findNameFromNumber(caller.number) || usePhonebookStore().findNameFromNumber(caller.number) || caller.name
		}

		logger.info(`SipClient: Incoming call ${caller.name} ${caller.number} -> ${callee.name} ${callee.number}`)

		const session = data.session as SipClientRTCSession

		if (!session.data) { session.data = {} }
		session.data.callId = data.request.getHeader('Call-ID')
		session.data.direction = 'INBOUND'
		session.data.originator = data.originator
		session.data.tcxState = SipClientTcxState.CALL_RING
		session.data.caller = caller
		session.data.callee = callee
		this.sessions.push(session)

		globalEvents.emit('SipClient', { event: SipClientEvent.INBOUND, session: session })

		// Reply with ringing state
		logger.debug('SipClient: Replying 180 Ringing');
		(data.request as any).reply(180, 'Ringing') // eslint-disable-line
		// reply(code, reason, extraHeaders, body, onSuccess, onFailure) {

		if (this.sessions.length === 1) {
			this.playRingTone(false, caller.number)
			if (this.headsets.jabra.use.value) { this.headsets.jabra.ringOn() }
			if (this.headsets.yealink.use.value) { this.headsets.yealink.ringOn(caller.name, caller.number) }
			if (this.headsets.sennheiser.use.value) { this.headsets.sennheiser.inboundStart(session.id) }
			this.headsets.kuando.ringing()
		} else {
			this.playRingTone(true, caller.number) // call waiting
		}

		// Fires when answer() has resolved getUserMedia and attached the local tracks
		session.on('connecting', () => { this.verifyMicrophoneDevice(session, 'inbound') })

		session.on('getusermediafailed', error => {
			logger.warning(`SipClient: getUserMedia failed for inbound call - ${(error as Error)?.message ?? error}`)
		})

		session.on('ended', data => {
			switch (data.cause) {
				case JsSIP.C.causes.BYE:
					this.onCallEnd(session, SipClientEndReason.BYE)
					break
				case JsSIP.C.causes.CANCELED:
					this.onCallEnd(session, SipClientEndReason.CANCELLED)
					break
				case JsSIP.C.causes.NO_ANSWER:
					this.onCallEnd(session, SipClientEndReason.NO_ANSWER)
					break
				case JsSIP.C.causes.EXPIRES:
					this.onCallEnd(session, SipClientEndReason.NO_ANSWER)
					break
				default:
					session.data.endCause = data.cause
					this.onCallEnd(session, SipClientEndReason.ERROR)
					break
			}
		})

		session.on('failed', data => {
			switch (data.cause) {
				case JsSIP.C.causes.BUSY:
					this.onCallEnd(session, SipClientEndReason.BUSY)
					break
				case JsSIP.C.causes.REJECTED:
					this.onCallEnd(session, SipClientEndReason.REJECTED)
					break
				case JsSIP.C.causes.REDIRECTED:
					this.onCallEnd(session, SipClientEndReason.REDIRECTED)
					break
				case JsSIP.C.causes.UNAVAILABLE:
					this.onCallEnd(session, SipClientEndReason.UNREACHABLE)
					break
				case JsSIP.C.causes.NOT_FOUND:
					this.onCallEnd(session, SipClientEndReason.NOT_FOUND)
					break
				case JsSIP.C.causes.NO_ANSWER:
					this.onCallEnd(session, SipClientEndReason.NO_ANSWER)
					break
				case JsSIP.C.causes.CANCELED:
					this.onCallEnd(session, SipClientEndReason.CANCELLED)
					break
				default:
					session.data.endCause = data.cause
					this.onCallEnd(session, SipClientEndReason.ERROR)
					break
			}
		})

		session.on('accepted', (data: { originator: Originator, response: IncomingResponse }) => { // 200 ok
			session.data.tcxState = SipClientTcxState.CALL_ANSWER
			globalEvents.emit('SipClient', { event: SipClientEvent.ANSWERED, session: session })
			logger.info(`SipClient: Inbound call accepted - ${data.response ? data.response.status_code : 'n/a'} ${data.response ? data.response.reason_phrase : 'n/a'}`)
			this.handleCallWaiting() // We want to handle call waiting tones here, if needed
			this.handleRtcFirewallIssues(session)
		})

		session.on('confirmed', (data: { originator: Originator, ack?: IncomingRequest }) => { // ACK
			logger.info(`SipClient: Inbound call confirmed (ACK received) - ${data.originator}`)
		})

		// | session.on('selectedcandidatepairchange', () => {
		// |   logger.info(`Triggered selectedcandidatepairchange: ${JSON.stringify(session.connection.getSenders()[0].transport.iceTransport)}`)
		// | })

		session.on('hold', _ => {
			session.data.tcxState = SipClientTcxState.CALL_HOLD
			globalEvents.emit('SipClient', { event: SipClientEvent.HOLD, session: session })
		})

		session.on('unhold', _ => {
			session.data.tcxState = SipClientTcxState.CALL_ANSWER
			globalEvents.emit('SipClient', { event: SipClientEvent.UNHOLD, session: session })
		})

		session.on('muted', _ => {
			globalEvents.emit('SipClient', { event: SipClientEvent.MUTED, session: session })
		}) // audio, video (bools)

		session.on('unmuted', _ => {
			globalEvents.emit('SipClient', { event: SipClientEvent.UNHOLD, session: session })
		}) // audio, video (bools)
	}

	/**
	 * Invoked when a call has ended
	 * @param session RTC session from jsSIP
	 * @param reason Reason why call ended: BUSY, REJECTED, REDIRECTED, UNREACHABLE, NOT_FOUND, BYE, CANCELLED, NO_ANSWER, ERROR
	 */
	private async onCallEnd(session: SipClientRTCSession, reason: SipClientEndReason) {
		logger.info(`SipClient: ${session.data.direction} call by ${session.data.originator} ended due to ${reason}`)

		this.stopRingTone()
		this.stopRingingTone()
		if (reason === SipClientEndReason.BUSY || reason === SipClientEndReason.UNREACHABLE || reason === SipClientEndReason.ERROR || reason === SipClientEndReason.NOT_FOUND) {
			this.playBusyTone(session)
		} else if (this.sessions.length === 1) {
			logger.info(`SipClient: ${session.data.direction} was the only call remaining - hanging up headset if used`)

			if (session.data.tcxState === 'CALL_HOLD') {
				// Call is held, unhold it before hanging up
				if (this.headsets.jabra.use.value) { this.headsets.jabra.unhold() }
				if (this.headsets.yealink.use.value) { this.headsets.yealink.unhold() }
				if (this.headsets.sennheiser.use.value) { this.headsets.sennheiser.unhold(session.id) }
				await sleep(200)
			}

			if (this.headsets.jabra.use.value) { this.headsets.jabra.onHook() }
			if (this.headsets.yealink.use.value) { this.headsets.yealink.onHook() }
			if (this.headsets.sennheiser.use.value) { this.headsets.sennheiser.hangup(session.id) }
			this.headsets.kuando.idle()
		} else if (this.headsets.sennheiser.use.value && this.sessions.length > 0) {
			this.headsets.sennheiser.hangup(session.id)
		} else {
			logger.info(`SipClient: ${session.data.direction} was not the only remaining call - not hanging up headset`)
		}

		// Stop the locally acquired microphone stream - JsSIP only stops streams it created itself
		if (session.data.localMediaStream) {
			for (const track of session.data.localMediaStream.getTracks()) { track.stop() }
			delete session.data.localMediaStream
		}

		session.data.tcxState = SipClientTcxState.CALL_END
		// Order is important - the async call has to be last, so the checking of this.sessions' length and the filtering are done atomically
		this.sessions = this.sessions.filter(ses => ses.id !== session.id)
		globalEvents.emit('SipClient', { event: SipClientEvent.END, session: session, reason: reason })
		await this.destroyRemoteAudioPlayer(session)
		await this.handleCallWaiting(session)
		await this.disableVoiceChanger()
	}

	/**
	 * If we have a ringing call, then whenever we pick up a new call, end a call etc, we should play the call waiting tone. If we have no calls left, we should play a ringing gtone
	 */
	private async handleCallWaiting(previousCall?: SipClientRTCSession) {
		logger.debug('SipClient.handleCallWaiting: Running handleCallWaiting')
		const ringingCall = this.sessions.find(session => session.data.tcxState === SipClientTcxState.CALL_RING)
		const hasActiveCall = this.sessions.find(session => [SipClientTcxState.CALL_ANSWER, SipClientTcxState.CALL_HOLD].includes(session.data.tcxState as SipClientTcxState))

		// If we have ended a call, and have ringing calls, we should play the ring tone (or call waiting, if we still have an answered call)
		if (ringingCall) {
			logger.debug('SipClient.handleCallWaiting: Found a ringing call')

			// We just destroyed the ring tone player, but if we still have a ringing call, we should play a ringtone - depending on if we have an answered call or not
			const inAnsweredCall = this.sessions.some(session => session.data.tcxState === SipClientTcxState.CALL_ANSWER)

			this.playRingTone(inAnsweredCall, ringingCall.data.caller?.number as string)

			if (this.headsets.jabra.use.value) {
				if (!hasActiveCall) {
					logger.debug('SipClient.handleCallWaiting: No active call - hanging up before ringing')
					this.headsets.jabra.onHook()
				}
				setTimeout(() => {
					if (this.sessions.some(session => session.data.tcxState === SipClientTcxState.CALL_RING)) {
						this.headsets.jabra.ringOn()
					}
				}, 500)
			}

			if (this.headsets.yealink.use.value) {
				if (!hasActiveCall) {
					logger.debug('SipClient.handleCallWaiting: No active call - hanging up before ringing')
					this.headsets.yealink.onHook()
				}
				setTimeout(() => {
					if (this.sessions.some(session => session.data.tcxState === SipClientTcxState.CALL_RING)) {
						this.headsets.yealink.ringOn(ringingCall.data.caller?.name, ringingCall.data.caller?.number)
					}
				}, 500)
			}

			if (this.headsets.sennheiser.use.value) {
				if (!hasActiveCall) {
					logger.debug('SipClient.handleCallWaiting: No active call - hanging up before ringing')
					if (previousCall) {
						this.headsets.sennheiser.hangup(previousCall.id)
					}
				}
				setTimeout(() => {
					if (this.sessions.some(session => session.data.tcxState === SipClientTcxState.CALL_RING)) {
						this.headsets.sennheiser.inboundStart(ringingCall.id)
					}
				}, 500)
			}
		} else {
			logger.debug('SipClient.handleCallWaiting: No ringing calls - not gonna play a call waiting tone or ringing tone')
		}
	}

	/**
	 * Initialize an audio player for the remote audio
	 * @param session RTC session
	 * @param stream Stream containing the audio
	 */
	private async createRemoteAudioPlayer(session: SipClientRTCSession, stream: MediaStream) {
		const id = 'SipPhoneRemoteAudio-' + Math.random().toString().substring(2)
		const parent = document.getElementById('SipPhoneVideoContainer')
		const audio = document.createElement('audio')

		audio.id = id
		audio.style.display = 'none'
		parent?.appendChild(audio)
		logger.debug('SipClient: Creating audio player for remote audio with id ' + id)

		if (isChrome && mediaDevices.speakerDeviceId) {
			logger.debug(`SipClient: Chrome environment detected, setting speaker device to ${mediaDevices.speakerDeviceId}`)
			audio.addEventListener('canplay', async () => {
				try { await audio.setSinkId(mediaDevices.speakerDeviceId) }
				catch (_) { logger.error(`SipClient: Failed to set audio device to ${mediaDevices.speakerDeviceId}`) }
			}, { once: true })
		}
		audio.srcObject = stream
		audio.play()
		if (!session.data) { session.data = {} }
		session.data.audioElement = audio
	}

	/**
	 * Terminate an audio player for the remote audio
	 * @param session RTC session
	 */
	private async destroyRemoteAudioPlayer(session: SipClientRTCSession) {
		if (!session.data?.audioElement) { return }
		const parent = document.getElementById('SipPhoneVideoContainer')
		const ae = session.data.audioElement

		ae.pause()
		ae.src = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAVFYAAFRWAAABAAgAZGF0YQAAAAA=' // Silence
		parent?.removeChild(session.data.audioElement)

		try {
			await session.data.audioElement.setSinkId('default')
			delete session.data.audioElement
			// Sometimes the pause does not work, so we try to pause it again after a short delay
			setTimeout(() => {
				ae.pause()
				ae.src = ''
			}, 500)
			logger.info(`SipClient: Destroying audio player for remote audio with id: ${ae.id}`)
		} catch (_) {
			logger.error('SipClient: Error setting sinkId to default in destroyRemoteAudioPlayer - is ignored for now')
		}
	}

	/**
	 * Audio constraint for the selected microphone. Electron ignores a bare deviceId
	 * (it is only an "ideal" hint) and silently opens the default microphone, so a
	 * specific selection must always be expressed as a required (exact) constraint.
	 */
	private microphoneConstraint(): boolean | MediaTrackConstraints {
		const id = mediaDevices.microphoneDeviceId

		return (id && id !== 'default') ? { deviceId: { exact: id } } : true
	}

	/**
	 * Open the selected microphone with a required (exact) deviceId constraint.
	 * The stream is acquired here and handed to JsSIP instead of letting JsSIP call
	 * getUserMedia itself, so a fallback to the default microphone only ever happens
	 * explicitly and with a warning - never silently.
	 * @param context Log context: outbound or inbound
	 * @returns The stream, or null if no microphone could be opened at all
	 */
	private async acquireMicrophoneStream(context: string): Promise<MediaStream | null> {
		const constraint = this.microphoneConstraint()

		if (constraint !== true) {
			try {
				return await navigator.mediaDevices.getUserMedia({ audio: constraint, video: false })
			}
			catch (err) {
				logger.warning(`SipClient: Could not open selected microphone ${mediaDevices.microphoneDevice?.label ?? 'n/a'} ` +
					`(${mediaDevices.microphoneDeviceId}) for ${context} call - falling back to the default microphone - ${(err as Error).name}: ${(err as Error).message}`)
			}
		}

		try {
			return await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
		}
		catch (err) {
			logger.error(`SipClient: Could not open any microphone for ${context} call - ${(err as Error).name}: ${(err as Error).message}`)
			return null
		}
	}

	/**
	 * Log which microphone the call is actually capturing from vs. the selected one.
	 * Chromium treats a bare deviceId constraint as optional and may silently fall
	 * back to the default device - this makes such a fallback visible in the logs.
	 * @param session RTC session
	 * @param context Log context: outbound, inbound or switch
	 */
	private verifyMicrophoneDevice(session: SipClientRTCSession, context: string) {
		try {
			const requestedId = mediaDevices.microphoneDeviceId
			const requestedLabel = mediaDevices.microphoneDevice?.label ?? 'n/a'
			const track = session.connection?.getSenders()[0]?.track

			if (!track) {
				logger.warning(`SipClient: Microphone check (${context}): no local audio track on session`)
				return
			}

			const actualId = track.getSettings().deviceId ?? 'n/a'

			if (actualId === requestedId) {
				logger.info(`SipClient: Microphone check (${context}): using ${track.label} (${actualId}) as requested`)
			} else {
				logger.warning(`SipClient: Microphone check (${context}): MISMATCH - requested ${requestedLabel} (${requestedId}) but capturing from ${track.label} (${actualId})`)
			}
		} catch (err) {
			logger.warning(`SipClient: Microphone check (${context}) failed - ${(err as Error).message}`)
		}
	}

	/** Switch audio device currently used for call */
	async switchAudioDevice() {
		if (isChrome) {
			logger.info(`SipClient: Switching audio device to: speaker ${mediaDevices.speakerDevice?.label} (${mediaDevices.speakerDeviceId}) /` +
				` microphone ${mediaDevices.microphoneDevice?.label} (${mediaDevices.microphoneDeviceId})`)
		}

		for (const session of this.sessions) {
			if (isChrome && mediaDevices.speakerDeviceId && session.data.audioElement) {
				logger.debug(`SipClient: Setting speaker device to ${mediaDevices.speakerDeviceId} for session: ${session.id}`)
				await session.data.audioElement.setSinkId(mediaDevices.speakerDeviceId)
			}

			if (mediaDevices.microphoneDeviceId) {
				const sender = session.connection?.getSenders()[0]

				if (!sender) { continue }

				let mediaStream: MediaStream

				try {
					mediaStream = await navigator.mediaDevices.getUserMedia({ audio: this.microphoneConstraint(), video: false })
				}
				catch (err) {
					// Do not fall back to the default microphone mid-call - keep the current one
					logger.warning(`SipClient: Could not open microphone ${mediaDevices.microphoneDevice?.label ?? 'n/a'} ` +
						`(${mediaDevices.microphoneDeviceId}) - keeping the current microphone - ${(err as Error).name}: ${(err as Error).message}`)
					continue
				}

				try {
					const oldTrack = sender.track
					const newTrack = mediaStream.getTracks()[0] as MediaStreamTrack

					newTrack.enabled = oldTrack?.enabled ?? true // Preserve mute state
					await sender.replaceTrack(newTrack)
					oldTrack?.stop()

					if (session.data.localMediaStream) {
						for (const track of session.data.localMediaStream.getTracks()) { track.stop() }
					}
					session.data.localMediaStream = mediaStream
					this.verifyMicrophoneDevice(session, 'switch')
				}
				catch (err) {
					for (const track of mediaStream.getTracks()) { track.stop() }
					logger.info(`SipClient: Failed to replace audio input device for current call: ${(err as Error).message}`)
				}
			}
		}
	}

	// TODO: Experimental - not tested
	async enableVoiceChanger() {
		if (this.voiceChangerEnabled) { await this.disableVoiceChanger() }
		let errored = false

		for (const session of this.sessions) {
			// First, get your SIP/WebRTC sender
			const senders = session.connection.getSenders()

			// Get the microphone stream using your selected device
			const stream = await navigator.mediaDevices.getUserMedia({ audio: this.microphoneConstraint(), video: false })

			// Create an AudioContext and the source from the microphone stream
			const audioContext = new AudioContext()
			const source = audioContext.createMediaStreamSource(stream)

			// Create your audio effect node(s) – for example, a simple filter to alter your voice
			const filter = audioContext.createBiquadFilter()

			filter.type = 'highpass'
			filter.frequency.value = 3000 // Adjust to achieve your desired "mouse-like" effect

			// Connect the nodes: microphone -> filter (or additional processing nodes)
			source.connect(filter)

			// Create a destination node that outputs a new MediaStream with the processed audio
			const destination = audioContext.createMediaStreamDestination()

			filter.connect(destination)

			// Now replace the sender’s track with the processed audio track from destination
			try { await senders[0]?.replaceTrack(destination.stream.getTracks()[0] as MediaStreamTrack) }
			catch (err) {
				logger.info(`SipClient: VoiceChanger failed to replace audio input device for current call: ${(err as Error).message}`)
				errored = true
			}

			this.voiceChangers.push({
				audioContext: audioContext,
				source: source,
				filter: filter,
				destination: destination
			})
		}

		if (!errored && this.sessions.length > 0) { this.voiceChangerEnabled = true }
	}

	// TODO: Experimental - not tested
	async disableVoiceChanger() {
		for (const session of this.sessions) {
			const senders = session.connection.getSenders()
			const mediaStream = await navigator.mediaDevices.getUserMedia({ audio: this.microphoneConstraint(), video: false })

			try {
				await senders[0]?.replaceTrack(mediaStream.getTracks()[0] as MediaStreamTrack)
			}
			catch (err) {
				logger.info(`SipClient: Failed to replace audio input device for current call: ${(err as Error).message}`)
			}
		}

		for (const vcs of this.voiceChangers) {
			try {
				vcs.audioContext.close()
				vcs.source.disconnect()
				vcs.filter.disconnect()
				vcs.destination.disconnect()
			}
			catch (_) { /** Ignore */ }
		}
		this.voiceChangers = []

		this.voiceChangerEnabled = false
	}

	/**
	 * Parse a SIP URI into a number and name
	 * @param raw Raw SIP URI
	 * @returns Object with number and name
	 */
	private parseUri(raw: string) {
		const res = { name: '', number: '' }
		const match = RegExp(/<sip:[^@:>;]+/).exec(raw)

		if (match) { res.number = match[0].substring(5) }
		res.name = raw.split('<')[0]?.trim().replace(/"/g, '') ?? ''
		return res
	}

	/**
	 * Lookup a session based on call-id or caller number or state
	 * @param callId Call-ID of call
	 * @param callerNumber Callers phone number
	 * @param callState TCX Call State
	 * @returns RTC session
	 */
	getSession(callId?: string, callerNumber?: string, callState?: SipClientTcxState | PbxCallState) {
		if (callId) {
			// Find session using callId (I made the call)
			const session = this.sessions.find(session => session.data.callId === callId)
			if (session) { return session }
		}

		if (callerNumber) {
			// Find call using callerNumber and callState (I received the call)
			let session = this.sessions.find(session =>
				callerNumber.endsWith(session.data.caller?.number as string) &&
				session.data.tcxState === callState &&
				session.data.direction === 'INBOUND'
			)
			if (session) { return session }

			// Check sipcall - in case we are looking for a call on hold and the sipcall does not know that it is on hold
			session = this.sessions.find(session =>
				callerNumber.endsWith(session.data.caller?.number as string) &&
				callState === SipClientTcxState.CALL_HOLD &&
				session.data.tcxState === SipClientTcxState.CALL_ANSWER &&
				session.data.direction == 'INBOUND'
			)
			if (session) { return session }

			// Find call using callState (I received the call)
			session = this.sessions.find(session => session.data.tcxState === callState && session.data.direction === 'INBOUND')
			if (session) { return session }
		}

		return undefined
	}

	/**
	 * Make a call
	 * @param number Number to call
	 * @param name Name of callee, if known
	 */
	async call(number: string, name?: string) {
		if (!this.config || !this.ua) { return }

		logger.info(`SipClient: Outbound call initated towards ${number} using ${this.config.server}`)

		// Acquire the microphone here (exact device) - JsSIP's own getUserMedia would let
		// Electron silently pick the default microphone (see verifyMicrophoneDevice)
		const localStream = await this.acquireMicrophoneStream('outbound')

		const options = {
			pcConfig: {
				iceServers: [ { urls: ['stun:mgw1.telecomx.dk:3478', 'stun:mgw2.telecomx.dk:3478' ] } ]
			},
			mediaConstraints: { audio: true, video: false },
			mediaStream: localStream ?? undefined,
			rtcOfferConstraints: { forceUseStunResponse: true }
		}

		let session: SipClientRTCSession

		try {
			session = this.ua.call(`sip:${number}@${this.config.server.split('://')[1]}`, options as any) // eslint-disable-line
		}
		catch (err) {
			if (localStream) { for (const track of localStream.getTracks()) { track.stop() } }
			logger.error(`SipClient: Failed to initiate outbound call - ${(err as Error).message}`)
			return
		}

		if (!session.data) { session.data = {} }
		session.data.localMediaStream = localStream ?? undefined

		// Fires right after getUserMedia has resolved and the local tracks are attached
		session.on('connecting', () => { this.verifyMicrophoneDevice(session, 'outbound') })

		session.on('getusermediafailed', error => {
			logger.warning(`SipClient: getUserMedia failed for outbound call - ${(error as Error)?.message ?? error}`)
		})

		session.on('progress', (data: { originator: Originator, response: IncomingResponse }) => {
			if (data.response) {
				if (data.response.status_code === 183) {
					// Progress with audio
					session.data.tcxState = SipClientTcxState.CALL_RING
					this.stopRingTone()
					this.stopRingingTone()
					if (this.headsets.jabra.use.value) { this.headsets.jabra.offHook() }
					if (this.headsets.yealink.use.value) { this.headsets.yealink.offHook(session.data.callee?.name, session.data.callee?.number) }
					if (this.headsets.sennheiser.use.value) { this.headsets.sennheiser.outboundStart(session.id) }
				} else if (data.response.status_code == 180) {
					// Progress with ringing
					session.data.tcxState = SipClientTcxState.CALL_RING
					this.playRingingTone()
					if (this.headsets.jabra.use.value) { this.headsets.jabra.offHook() }
					if (this.headsets.yealink.use.value) { this.headsets.yealink.offHook(session.data.callee?.name, session.data.callee?.number) }
					if (this.headsets.sennheiser.use.value) { this.headsets.sennheiser.outboundStart(session.id) }
				}
			} else {
				session.data.tcxState = SipClientTcxState.CALL_START
			}

			logger.info(`SipClient: Outbound call progress - ${data.response?.status_code ?? 'n/a'} ${data.response?.reason_phrase ?? 'n/a'}`)
		})

		session.on('failed', data => {
			switch (data.cause) {
				case JsSIP.C.causes.BUSY: this.onCallEnd(session, SipClientEndReason.BUSY); break
				case JsSIP.C.causes.REJECTED: this.onCallEnd(session, SipClientEndReason.REJECTED); break
				case JsSIP.C.causes.REDIRECTED: this.onCallEnd(session, SipClientEndReason.REDIRECTED); break
				case JsSIP.C.causes.UNAVAILABLE: this.onCallEnd(session, SipClientEndReason.UNREACHABLE); break
				case JsSIP.C.causes.NOT_FOUND: this.onCallEnd(session, SipClientEndReason.NOT_FOUND); break
				case JsSIP.C.causes.CANCELED: this.onCallEnd(session, SipClientEndReason.CANCELLED); break
				default:
					session.data.endCause = data.cause
					this.onCallEnd(session, SipClientEndReason.ERROR)
					break
			}
		})

		session.on('accepted', (data: { originator: Originator, response: IncomingResponse }) => { // 200 ok
			if (this.headsets.sennheiser.use.value && session.data.tcxState === SipClientTcxState.CALL_START) { this.headsets.sennheiser.outboundStart(session.id) }
			session.data.tcxState = SipClientTcxState.CALL_ANSWER
			this.stopRingTone()
			this.stopRingingTone()
			if (this.headsets.jabra.use.value) { this.headsets.jabra.offHook() }
			if (this.headsets.yealink.use.value) { this.headsets.yealink.offHook(session.data.callee?.name, session.data.callee?.number, true) }
			if (this.headsets.sennheiser.use.value) { this.headsets.sennheiser.offHook(session.id) }
			this.headsets.kuando.busy()
			globalEvents.emit('SipClient', { event: SipClientEvent.ANSWERED, session: session })
			logger.info(`SipClient: Outbound call accepted - ${data.response?.status_code ?? 'n/a'} ${data.response?.reason_phrase ?? 'n/a'}`)
		})

		session.on('confirmed', (data: { originator: Originator, ack: IncomingRequest }) => { // ACK
			logger.debug(`SipClient: Outbound call confirmed (ACK sent) - ${data.originator}`)
		})

		session.on('ended', data => {
			switch (data.cause) {
				case JsSIP.C.causes.BYE: this.onCallEnd(session, SipClientEndReason.BYE); break
				case JsSIP.C.causes.CANCELED: this.onCallEnd(session, SipClientEndReason.CANCELLED); break
				case JsSIP.C.causes.NO_ANSWER: this.onCallEnd(session, SipClientEndReason.NO_ANSWER); break
				case JsSIP.C.causes.EXPIRES: this.onCallEnd(session, SipClientEndReason.NO_ANSWER); break
				default:
					session.data.endCause = data.cause
					this.onCallEnd(session, SipClientEndReason.ERROR)
					break
			}
		})

		let iceWatchdog: ReturnType<typeof setTimeout> | undefined

		session.on('icecandidate', event => {
			if (event.candidate.type === 'srflx' && event.candidate.relatedAddress !== null && event.candidate.relatedPort !== null) {
				logger.debug(`SipClient: ICE Candidate: ${JSON.stringify(event.candidate)}`)
				if (iceWatchdog) {
					clearTimeout(iceWatchdog)
					iceWatchdog = undefined
				}
				event.ready()
				return
			}

			// No srflx candidate yet - (re)arm the watchdog so the INVITE cannot be blocked forever
			// waiting for a STUN response that never comes. Calling ready() after JsSIP has already
			// sent the offer is a no-op, so a stale timer firing late is harmless.
			if (iceWatchdog) { clearTimeout(iceWatchdog) }
			iceWatchdog = setTimeout(() => {
				iceWatchdog = undefined
				logger.warning(`SipClient: No STUN (srflx) candidate within ${ICE_GATHERING_WATCHDOG_MS} ms of last ICE candidate - proceeding with local candidates only`)
				event.ready()
			}, ICE_GATHERING_WATCHDOG_MS)
		})

		session.on('hold', _ => {
			session.data.tcxState = SipClientTcxState.CALL_HOLD
			globalEvents.emit('SipClient', { event: SipClientEvent.HOLD, session: session })
		})

		session.on('unhold', _ => {
			session.data.tcxState = SipClientTcxState.CALL_ANSWER
			globalEvents.emit('SipClient', { event: SipClientEvent.UNHOLD, session: session })
		})

		session.on('muted', _ => {
			globalEvents.emit('SipClient', { event: SipClientEvent.MUTED, session: session })
		}) // audio, video (bools)

		session.on('unmuted', _ => {
			globalEvents.emit('SipClient', { event: SipClientEvent.UNHOLD, session: session })
		}) // audio, video (bools)

		if (!session.data) { session.data = {} }
		session.data.direction = 'OUTBOUND' // inbound, outbound
		session.data.originator = 'local' as Originator // local, remote
		session.data.tcxState = SipClientTcxState.CALL_START
		session.data.caller = { name: this.config.extensionName, number: this.config.extensionNumber }
		session.data.callee = { name: name ?? '', number: number }
		this.sessions.push(session)
		this.headsets.kuando.calling()
		globalEvents.emit('SipClient', { event: SipClientEvent.OUTBOUND, session: session })
	}

	/**
	 * Start playing the ringtone, either the normal ringtone or the call waiting ringtone (if the user is already in a call)
	 * @param useCallWaitingRingTone True to use call waiting ring tone, false to use normal ring tone
	 * @param callerNumber Callers phone number
	 */
	private playRingTone(useCallWaitingRingTone: boolean, callerNumber: string) {
		logger.debug(`SipClient: Shall play ${useCallWaitingRingTone ? 'call waiting' : 'ring'} tone for caller ${callerNumber}`)
		this.shouldBePlayingRingtone = true

		// We wait for 500 ms before playing the ringtone, to avoid playing it when the call has already been answered
		setTimeout(async () => {
			if (!this.shouldBePlayingRingtone) { return }

			// We should still play useCallWaitingRingTone, even if the user has not chosen a ring tone
			let ringtoneId = callerNumber.length < 8 ? ss.settings.ringtoneLocal : ss.settings.ringtoneExternal
			const cc = employees.me?.currentCall

			// If user has a special ringtone for queues....
			if (cc?.caller.queueId) {
				if (ss.settings.ringtoneQueue) { ringtoneId = ss.settings.ringtoneQueue }

				// If user has a special ringtone for a specific queue
				const customQueueRingtone = ss.settings.queueRingtones?.find(qr => qr.queue === cc.caller.queueId)
				if (customQueueRingtone?.ringtone) { ringtoneId = customQueueRingtone.ringtone }
			}

			// If user is already in a call we instead need the call-waiting ringtone
			if (useCallWaitingRingTone) { ringtoneId = CALL_WAITING_RINGTONE }

			if (ringtoneId) {
				logger.debug(`SipClient: Playing ${useCallWaitingRingTone ? 'call waiting' : 'ring'} tone`)

				let ae = document.getElementById('RingTonePlayer') as HTMLAudioElement
				if (!ae) {
					const parent = document.getElementById('SipPhoneVideoContainer')

					ae = document.createElement('audio')
					ae.id = 'RingTonePlayer'
					ae.autoplay = true
					ae.loop = true
					parent?.appendChild(ae)
				}

				if (useCallWaitingRingTone) {
					if (isChrome && mediaDevices.speakerDeviceId) {
						ae.addEventListener('canplay', async () => {
							logger.debug(`SipClient: Setting ringtone device to ${mediaDevices.speakerDevice?.label} (${mediaDevices.speakerDeviceId})`)

							try { await ae.setSinkId(mediaDevices.speakerDeviceId) }
							catch (_) { logger.error(`SipClient: Failed to set ringtone device to ${mediaDevices.speakerDeviceId}`) }
						}, { once: true })
					}
				} else if (isChrome && mediaDevices.ringerDeviceId) {
					ae.addEventListener('canplay', async () => {
						logger.debug(`SipClient: Setting ringtone device to ${mediaDevices.ringerDevice?.label} (${mediaDevices.ringerDeviceId})`)

						try { await ae.setSinkId(mediaDevices.ringerDeviceId) }
						catch (_) { logger.error(`SipClient: Failed to set ringtone device to ${mediaDevices.ringerDeviceId}`) }
					}, { once: true })
				}

				ae.src = `https://audio.telecomx.dk/${ringtoneId}.mp3`
				ae.currentTime = 0
				ae.volume = useCallWaitingRingTone ? 0.1 : mediaDevices.ringerVolume
				ae.play()
			}
		}, 500)
	}

	/** Stop ringtone / call waiting tone */
	private stopRingTone() {
		this.shouldBePlayingRingtone = false
		const ae = document.getElementById('RingTonePlayer') as HTMLAudioElement
		if (ae) {
			logger.info('SipClient: Stop playing ringtone')
			ae.pause()
			ae.loop = false
			ae.autoplay = false
			ae.src = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAVFYAAFRWAAABAAgAZGF0YQAAAAA='
			ae.parentNode?.removeChild(ae)
			setTimeout(() => { ae.pause() }, 500) // Fail-safe
		}
	}

	/** Start playing the ringing tone (played in your ear when you are calling someone) */
	private async playRingingTone() {
		logger.info('SipClient: Playing ringing tone')
		let ae = document.getElementById('RingingTonePlayer') as HTMLAudioElement
		if (!ae) {
			const parent = document.getElementById('SipPhoneVideoContainer')

			ae = document.createElement('audio')
			ae.id = 'RingingTonePlayer'
			ae.autoplay = true
			ae.loop = true
			parent?.appendChild(ae)
		}
		if (isChrome && mediaDevices.speakerDeviceId) {
			ae.addEventListener('canplay', async () => {
				logger.debug(`SipClient: Setting ringingtone device to ${mediaDevices.speakerDeviceId}`)

				try { await ae.setSinkId(mediaDevices.speakerDeviceId) }
				catch (_) { logger.error(`SipClient: Failed to set audio device to ${mediaDevices.speakerDeviceId}`) }
			}, { once: true })
		}

		ae.src = `https://audio.telecomx.dk/${RINGING_TONE}.mp3`
		ae.currentTime = 0
		ae.volume = 1
		ae.play()
	}

	/** Stop playing ringing tone (played in your ear when you are calling someone) */
	private stopRingingTone() {
		const ae = document.getElementById('RingingTonePlayer') as HTMLAudioElement
		if (ae) {
			logger.info('SipClient: Stop playing ringingtone')
			ae.pause()
			ae.parentNode?.removeChild(ae)
			setTimeout(() => { ae.pause() }, 500) // Fail-safe
		}
	}

	/**
	 * Start playing the busy tone (when you called someone and they are busy)
	 * @param session Call session
	 */
	private async playBusyTone(session: SipClientRTCSession) {
		logger.info('SipClient: Playing busy tone')

		let ae = document.getElementById('BusyTonePlayer') as HTMLAudioElement
		if (!ae) {
			const parent = document.getElementById('SipPhoneVideoContainer')

			ae = document.createElement('audio')
			ae.id = 'BusyTonePlayer'
			ae.autoplay = true
			ae.loop = false
			parent?.appendChild(ae)
		}

		// Release the headset once the busy tone is done. This MUST happen even if the audio fails to
		// load or play - otherwise the headset is left off-hook, stuck thinking a call is ongoing, and
		// the next call cannot be answered. Guarded so it runs exactly once per playback, and triggered
		// by 'ended', 'error', a rejected play() and a safety timeout, whichever comes first.
		// eslint-disable-next-line prefer-const -- assigned once below, but cannot be const: releaseHeadset's closure must reference it before the timer can be created
		let fallbackTimer: ReturnType<typeof setTimeout> | undefined
		let released = false
		const releaseHeadset = () => {
			if (released) { return }
			released = true
			clearTimeout(fallbackTimer)
			this.busyToneCleanup = undefined
			ae.removeEventListener('ended', releaseHeadset)
			ae.removeEventListener('error', releaseHeadset)
			if (this.headsets.jabra.use.value) { this.headsets.jabra.onHook() }
			if (this.headsets.yealink.use.value) { this.headsets.yealink.onHook() }
			if (this.headsets.sennheiser.use.value && session?.id) { this.headsets.sennheiser.hangup(session.id) }
			this.headsets.kuando.idle()
		}

		// The audio element is reused, so detach any handler left over from a previous (interrupted) tone
		// before binding this playback's handler - avoids stacking listeners and stale-session hangups.
		if (this.busyToneCleanup) {
			ae.removeEventListener('ended', this.busyToneCleanup)
			ae.removeEventListener('error', this.busyToneCleanup)
		}
		this.busyToneCleanup = releaseHeadset
		ae.addEventListener('ended', releaseHeadset)
		ae.addEventListener('error', releaseHeadset)
		fallbackTimer = setTimeout(releaseHeadset, BUSY_TONE_MAX_MS)

		try {
			if (mediaDevices.speakerDeviceId) {
				await ae.setSinkId(mediaDevices.speakerDeviceId)
			}
		}
		catch (err) {
			logger.warning(`SipClient: Failed to set busy tone output device - ${(err as Error).message}`)
		}

		ae.src = `https://audio.telecomx.dk/${BUSY_TONE}.mp3`
		ae.currentTime = 0
		ae.volume = 1

		try {
			await ae.play()
		}
		catch (err) {
			logger.warning(`SipClient: Failed to play busy tone - ${(err as Error).message}`)
			releaseHeadset()
		}
	}

	/**
	 * Play DTMF tone (so that the user can hear the tone)
	 * @param tone DTMF tone to play: 0 1 2 3 4 5 6 7 8 9 * #
	 */
	async playDtmfTone(tone: string) {
		logger.info(`SipClient: Playing DTMF ${tone}`)
		let ae = document.getElementById('DtmfTonePlayer') as HTMLAudioElement
		if (!ae) {
			const parent = document.getElementById('SipPhoneVideoContainer')

			ae = document.createElement('audio')
			ae.id = 'DtmfTonePlayer'
			ae.autoplay = true
			ae.loop = false
			parent?.appendChild(ae)
		}
		if (isChrome && mediaDevices.speakerDeviceId) {
			ae.addEventListener('canplay', async () => {
				logger.debug(`SipClient: Setting DTMF tone device to ${mediaDevices.speakerDevice?.label} (${mediaDevices.speakerDeviceId})`)

				try { await ae.setSinkId(mediaDevices.speakerDeviceId) }
				catch (_) { logger.error(`SipClient: Failed to set DTMF tone device to ${mediaDevices.speakerDeviceId}`) }
			}, { once: true })
		}

		ae.src = `https://audio.telecomx.dk/${DTMF_TONES[tone as keyof typeof DTMF_TONES]}.mp3`
		ae.currentTime = 0
		ae.volume = 0.5
		ae.play()
		setTimeout(() => { ae.pause() }, 200)
	}

	/**
	 * Answer an inbound call
	 * @param session RTC session
	 */
	async answer(session: SipClientRTCSession) {
		// Acquire the microphone here (exact device) - JsSIP's own getUserMedia would let
		// Electron silently pick the default microphone (see verifyMicrophoneDevice)
		const localStream = await this.acquireMicrophoneStream('inbound')

		const options = {
			mediaConstraints: { audio: true, video: false },
			mediaStream: localStream ?? undefined,
			rtcAnswerConstraints: { forceUseStunResponse: true },
			pcConfig: {
				iceServers: [ { urls: ['stun:mgw1.telecomx.dk:3478', 'stun:mgw2.telecomx.dk:3478' ] } ],
				iceCandidatePoolSize: 1
			}
		}

		this.stopRingTone()
		this.stopRingingTone()

		try {
			session.answer(options as any) // eslint-disable-line
			session.data.localMediaStream = localStream ?? undefined
			logger.info('SipClient: Inbound call answered')
		}
		catch (err) {
			if (localStream) { for (const track of localStream.getTracks()) { track.stop() } }
			logger.error(`SipClient: Failed to answer inbound call - ${(err as { code: number }).code} - ${(err as Error).message}`)
		}

		const rtcPeerConnection = session.connection

		if (!rtcPeerConnection) {
			logger.error('SipClient: rtcPeerConnection missing')
			return
		}

		rtcPeerConnection.addEventListener('track', async event => {
			logger.info(`SipClient: Track found for inbound call - kind: ${event.track.kind} - label: ${event.track.label} - enabled: ${event.track.enabled}`)
			this.createRemoteAudioPlayer(session, event.streams[0] as MediaStream)
		})

		this.headsets.kuando.busy()
		if (this.headsets.jabra.use.value) { this.headsets.jabra.offHook() }
		if (this.headsets.yealink.use.value) { this.headsets.yealink.offHook(session.data.caller?.name, session.data.caller?.number, true) }
		if (this.headsets.sennheiser.use.value) { this.headsets.sennheiser.offHook(session.id) }
	}

	/**
	 * Send DTMF tone to the other party
	 * @param key DTMF key to send: 0 1 2 3 4 5 6 7 8 9 * #
	 * @param session RTC session
	 */
	sendDTMF(key: string, session: SipClientRTCSession) {
		logger.info(`SipClient: Sending DTMF ${key}`)
		session.sendDTMF(key)
	}

	/**
	 * Put a call on hold
	 * @param session RTC session
	 */
	hold(session: SipClientRTCSession) {
		session.hold({}, () => {
			logger.info('SipClient: Hold start')
			if (this.headsets.jabra.use.value) { this.headsets.jabra.hold() }
			if (this.headsets.sennheiser.use.value) { this.headsets.sennheiser.hold(session.id) }
			if (this.headsets.yealink.use.value) { this.headsets.yealink.hold() }
		})
	}

	/**
	 * Resume a call from hold
	 * @param session RTC session
	 */
	unhold(session: SipClientRTCSession) {
		session.unhold({}, () => {
			logger.info('SipClient: Hold end')
		})
		if (this.headsets.jabra.use.value) { this.headsets.jabra.unhold() }
		if (this.headsets.sennheiser.use.value) { this.headsets.sennheiser.unhold(session.id) }
		if (this.headsets.yealink.use.value) { this.headsets.yealink.unhold() }
		this.headsets.kuando.busy()
	}

	/**
	 * Mute the microphone
	 * @param session RTC session
	 */
	mute(session: SipClientRTCSession) {
		logger.info('SipClient: Mute start')
		session.mute({ audio: true })
		if (this.headsets.yealink.use.value) { this.headsets.yealink.mute() } // shows the mute state on the device - also the required ack for a device-initiated mute
	}

	/**
	 * Unmute the microphone
	 * @param session RTC session
	 */
	unmute(session: SipClientRTCSession) {
		logger.info('SipClient: Mute end')
		session.unmute({ audio: true })
		if (this.headsets.yealink.use.value) { this.headsets.yealink.unmute() } // shows the unmute state on the device - also the required ack for a device-initiated unmute
	}

	/**
	 * Hangup a call
	 * @param session RTC session
	 */
	hangup(session: SipClientRTCSession) {
		logger.info('SipClient: Hangup initiated')

		try {
			session.terminate()
			logger.info('SipClient: Hangup completed')
		}
		catch (err) { logger.warning(`SipClient: Hangup failed - ${(err as { code: number }).code} - ${(err as Error).message}`) }
	}

	/**
	 * Decline/reject an incoming call
	 * @param session RTC session
	 */
	decline(session: SipClientRTCSession) {
		logger.info('SipClient: Decline initiated')

		try {
			session.terminate()
			logger.info('SipClient: Decline completed')
		}
		catch (err) {
			logger.warning(`SipClient: Decline failed - ${(err as { code: number }).code} - ${(err as Error).message}`)
		}
	}

	/**
	 * Triggered by headsets when the user presses a button
	 * @param event Event from headset
	 * @param value Optional value for the event - the digit for DTMF
	 */
	headsetEventHandler(event: 'ANSWER' | 'HANGUP' | 'REJECT' | 'HOLD' | 'UNHOLD' | 'MUTE' | 'UNMUTE' | 'DTMF', value?: string) {
		switch (event) {
			case 'ANSWER': this.headsetAnswerEvent(); break
			case 'HANGUP': this.headsetHangupEvent(); break
			case 'REJECT': this.headsetRejectEvent(); break
			case 'HOLD': this.headsetActivateHoldEvent(); break
			case 'UNHOLD': this.headsetDeactivateHoldEvent(); break
			case 'MUTE': this.headsetMuteEvent(true); break
			case 'UNMUTE': this.headsetMuteEvent(false); break
			case 'DTMF': if (value) { this.headsetDtmfEvent(value) } break
		}
	}

	/** Called by headsets when the user pressed the answer button */
	private headsetAnswerEvent() {
		logger.debug('SipClient: headsetAnswerEvent called by headset')

		const relevantSessions = this.sessions.filter(session => session.data.tcxState && [SipClientTcxState.CALL_RING, SipClientTcxState.CALL_HOLD].includes(session.data.tcxState))
		const session = relevantSessions[0]

		if (!session) {
			logger.debug(`SipClient: No relevant calls to answer from the ${relevantSessions.length} calls - exiting`)
			return
		}

		if (session.data.tcxState === SipClientTcxState.CALL_RING) {
			logger.debug(`SipClient: Only one call - answering ${session.id}`)
			globalEvents.emit('Call:Answer', { sessionId: session.id }) // There is only 1 call, just answer that one
		} else {
			logger.debug(`SipClient: No suitable call to answer because it is in ${session.data.tcxState}, and there is only 1 call - exiting`)
		}
	}

	/** Called by headsets when the user presses the hangup button */
	private headsetHangupEvent() {
		logger.debug('SipClient: headsetHangupEvent called')

		if (this.sessions.length > 1) {
			logger.debug(`SipClient: There are ${this.sessions.length} calls, so we prioritize hanging up the outgoing ringing call -> answered call -> call on hold`)

			const outgoingRingingCall = this.sessions.find(session =>
				session.data.tcxState &&
				([SipClientTcxState.CALL_START, SipClientTcxState.CALL_RING].includes(session.data.tcxState)) &&
				session.data.direction === 'OUTBOUND'
			)

			if (outgoingRingingCall) {
				logger.debug(`SipClient: Hanging up outbound ringing call ${outgoingRingingCall.id} (${outgoingRingingCall.data.callee?.number})`)
				globalEvents.emit('Call:Hangup', { sessionId: outgoingRingingCall.id })
				return
			}

			const answeredCall = this.sessions.find(session => session.data.tcxState === SipClientTcxState.CALL_ANSWER)

			if (answeredCall) {
				logger.debug(`SipClient: Hanging up answered call ${answeredCall.id} (${answeredCall.data.caller?.number} -> ${answeredCall.data.callee?.number})`)
				globalEvents.emit('Call:Hangup', { sessionId: answeredCall.id })
				return
			}

			const callOnHold = this.sessions.find(session => session.data.tcxState === SipClientTcxState.CALL_HOLD)

			if (callOnHold) {
				logger.debug(`SipClient: Hanging up held call ${callOnHold.id} (${callOnHold.data.caller?.number} -> ${callOnHold.data.callee?.number})`)
				globalEvents.emit('Call:Hangup', { sessionId: callOnHold.id })
				return
			}
		}

		const latestCall = this.sessions[this.sessions.length - 1]

		switch (latestCall?.data.tcxState) {
			case SipClientTcxState.CALL_START:
				logger.debug(`SipClient: Cancelling our outgoing call ${latestCall.id}`)
				globalEvents.emit('Call:Hangup', { sessionId: latestCall.id })
				break
			case SipClientTcxState.CALL_ANSWER:
				logger.debug(`SipClient: Hanging up the current answered call ${latestCall.id}`)
				globalEvents.emit('Call:Hangup', { sessionId: latestCall.id })
				break
			case SipClientTcxState.CALL_RING:
				logger.debug(`SipClient: Hanging up the ringing call ${latestCall.id}`)
				globalEvents.emit('Call:Reject', { sessionId: latestCall.id })
				break
			case SipClientTcxState.CALL_HOLD:
				logger.debug(`SipClient: Hanging up the call on hold ${latestCall.id}`)
				globalEvents.emit('Call:Hangup', { sessionId: latestCall.id })
				break
		}
	}

	/** Called by headsets when the user rejects a call */
	private headsetRejectEvent() {
		logger.debug('SipClient: headsetRejectEvent called')
		const rejectableStates = [ SipClientTcxState.CALL_START, SipClientTcxState.CALL_RING ]
		const ringingSessions = this.sessions.filter(session => session.data.tcxState && rejectableStates.includes(session.data.tcxState))

		if (ringingSessions.length > 0) {
			logger.debug(`SipClient: Rejecting ringing call ${ringingSessions[0]?.id}`)
			globalEvents.emit('Call:Reject', { sessionId: ringingSessions[0]?.id as string })
			return
		}

		const answeredSessions = this.sessions.filter(session => session.data.tcxState && session.data.tcxState === SipClientTcxState.CALL_ANSWER)

		if (answeredSessions.length > 0) {
			logger.debug(`SipClient: Rejecting answered call ${answeredSessions[0]?.id}`)
			globalEvents.emit('Call:Reject', { sessionId: answeredSessions[0]?.id as string }) // There is only 1 call, just answer that one
		}
	}

	/** Called by headsets when the user wants to put a call on hold */
	private headsetActivateHoldEvent() {
		logger.debug('SipClient: headsetActivateHoldEvent called')
		const activeCall = this.sessions.find(session => session.data.tcxState === SipClientTcxState.CALL_ANSWER)

		if (activeCall) {
			globalEvents.emit('Call:ActivateHold', { sessionId: activeCall.id })
		}
	}

	/** Called by headsets when the user wants to resume a held call */
	private headsetDeactivateHoldEvent() {
		logger.debug('SipClient: headsetDeactivateHoldEvent called')
		const callOnHold = this.sessions.find(session => session.data.tcxState === SipClientTcxState.CALL_HOLD)

		if (callOnHold) {
			globalEvents.emit('Call:DeactivateHold', { sessionId: callOnHold.id })
		}
	}

	/** Called by headsets when the user presses the mute button */
	private headsetMuteEvent(mute: boolean) {
		logger.debug(`SipClient: headsetMuteEvent called - mute: ${mute}`)
		const activeCall = this.sessions.find(session => session.data.tcxState === SipClientTcxState.CALL_ANSWER)

		if (!activeCall) { return }

		if (mute) {
			this.mute(activeCall)
		} else {
			this.unmute(activeCall)
		}
	}

	/** Called by headsets when the user presses a dialpad key during a call (for IVR menus) */
	private headsetDtmfEvent(digit: string) {
		logger.debug(`SipClient: headsetDtmfEvent called - digit: ${digit}`)
		const activeCall = this.sessions.find(session => session.data.tcxState === SipClientTcxState.CALL_ANSWER)

		if (activeCall) {
			this.sendDTMF(digit, activeCall)
		}
	}

	/**
	 * This code is for handling that firewalls etc. can block UDP traffic made by WebRTC.
	 * If we can detect that the call hasn't received any RTP data yet (after a small period),
	 * then we need to try to renegotiate the WebRTC connection (or possibly try a SIP re-invite).
	 */
	handleRtcFirewallIssues(session: SipClientRTCSession) {
		setTimeout(async () => {
			try {
				// We should never need to renegotiate more than 2 times (at max), but better safe than sorry
				for (let renegotiateIndex = 0; renegotiateIndex < MAX_RENEGOTATIONS; renegotiateIndex++) {
					if (session?.connection && session.data.tcxState === SipClientTcxState.CALL_ANSWER) {
						// Figure out if we have received any ingoing data yet, which indicates whether there's punched a hole in our firewall/anti-virus/whatever
						for (let failedRetryIndex = 0; failedRetryIndex < MAX_RETRIES_FAILED_RTC; failedRetryIndex++) {
							const stats = await session.connection.getStats()

							for (const [_id, report] of stats.entries()) {
								if (report.type !== 'inbound-rtp') { continue }
								if (report?.bytesReceived > 0) {
									return // Happy path, the call has data going through
								}
								// If we can't find the session yet, or bytes received is still zero, we just retry
							}
							/**
							 * All calls get a reinvite after 500 ms for Asterisk reasons, so we need to make sure that we
							 * don't send the renegotiation before 500 ms after the call is answered (from we get ACK from the SIP-server).
							 * So MAX_RETRIES_FAILED_RTC * the sleep timer below + the setTimeout delay for this function should not be below 500ms.
							 *
							*/
							await sleep(70)
						}
						logger.info('SipClient: Renegotiation started')
						// | session._rtcOfferConstraints = { forceUseStunResponse: true }; // Doesn't seem needed atm, possibly in the future
						session.connection.restartIce() // Both restartIce and renegotiate seem to be needed.
						const status = session.renegotiate({ rtcOfferConstraints: { forceUseStunResponse: true } } as any) // eslint-disable-line

						logger.info(`SipClient: Renegotiation done with status = ${status}, also restarting ICE`)
						await sleep(2000)
					} else {
						return
					}
				}
			} catch (err) {
				logger.error(`SipClient: Error happened in timeout after call answer: message: ${(err as Error).message}\nstack: ${(err as Error).stack}`)
			}
		}, 100)
	}
}
